/**
 * LLM provider API client — CRUD operations for managing LLM provider configurations.
 */

import { logger } from '@/utils/logger';

import { api } from './api';

import type { ImageConfigSchema } from '@features/chat/components/imageConfigSchema';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LLMProvider {
  id: string;
  name: string;
  provider: string;
  modelType?: string;
  description?: string;
  contextWindow?: number;
  maxTokens?: number;
  costPerToken?: string;
  environmentKey?: string;
  category?: string | null;
  isActive: boolean;
  isDefault: boolean;
  isVisible?: boolean;
  isUtilityModel?: boolean;
  sortOrder: number;
  supportsText?: boolean;
  supportsAudio?: boolean;
  supportsVideo?: boolean;
  supportsVision?: boolean;
  supportsFunctionCalling?: boolean;
  supportsStreaming?: boolean;
  metadata?: Record<string, unknown>;
  configSchema?: ImageConfigSchema | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLLMProviderRequest {
  id: string;
  name: string;
  provider: string;
  model_type?: string | undefined;
  description?: string | undefined;
  context_window?: number | undefined;
  max_tokens?: number | undefined;
  cost_per_token?: string | undefined;
  environment_key?: string | undefined;
  category?: string | null | undefined;
  is_active?: boolean | undefined;
  is_default?: boolean | undefined;
  is_visible?: boolean | undefined;
  sort_order?: number | undefined;
  metadata?: Record<string, unknown> | undefined;
  config_schema?: ImageConfigSchema | null | undefined;
}

export interface UpdateLLMProviderRequest {
  name?: string | undefined;
  model_type?: string | undefined;
  description?: string | undefined;
  context_window?: number | undefined;
  max_tokens?: number | undefined;
  cost_per_token?: string | undefined;
  environment_key?: string | undefined;
  category?: string | null | undefined;
  is_active?: boolean | undefined;
  is_default?: boolean | undefined;
  is_visible?: boolean | undefined;
  sort_order?: number | undefined;
  metadata?: Record<string, unknown> | undefined;
  config_schema?: ImageConfigSchema | null | undefined;
}

interface LLMProviderResponse {
  data: LLMProvider[];
  success: boolean;
  message: string;
}

interface LLMProviderGroupedResponse {
  data: Record<string, LLMProvider[]>;
  success: boolean;
  message: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Wraps an async API call with error logging and re-throwing.
 *
 * Executes the provided async function and, if it throws, logs the error
 * with the specified message via the application logger before re-throwing.
 *
 * @param fn - The async function to execute.
 * @param errorMsg - A descriptive error message to log if the call fails.
 * @returns The resolved value from the async function.
 * @throws Re-throws the original error after logging.
 */
async function llmApiCall<T>(fn: () => Promise<T>, errorMsg: string): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    logger.error(errorMsg, error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

/**
 * API client for CRUD operations on LLM provider configurations.
 *
 * Provides methods to list, fetch, create, update, delete, and set
 * default LLM providers. All methods include error logging.
 */
export const llmProviders = {
  async getAll(activeOnly: boolean = true): Promise<LLMProvider[]> {
    return llmApiCall(async () => {
      const response = await api.get<LLMProviderResponse>(
        `/api/llm-providers/?active_only=${activeOnly}`
      );
      return response.data.data || [];
    }, 'Failed to get LLM providers:');
  },

  async getGrouped(): Promise<Record<string, LLMProvider[]>> {
    return llmApiCall(async () => {
      const response = await api.get<LLMProviderGroupedResponse>('/api/llm-providers/grouped');
      return response.data.data || {};
    }, 'Failed to get grouped LLM providers:');
  },

  async getDefault(): Promise<LLMProvider | null> {
    try {
      const response = await api.get<{ data: LLMProvider | null }>('/api/llm-providers/default');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to get default LLM provider:', error);

      throw error;
    }
  },

  async getById(providerId: string): Promise<LLMProvider> {
    try {
      const response = await api.get<{ data: LLMProvider }>(`/api/llm-providers/${providerId}`);
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to get LLM provider ${providerId}:`, error);

      throw error;
    }
  },

  async setDefault(providerId: string): Promise<boolean> {
    try {
      const response = await api.post<{ data: boolean }>(
        `/api/llm-providers/${providerId}/set-default`,
        {}
      );
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to set default LLM provider ${providerId}:`, error);

      throw error;
    }
  },

  async create(data: CreateLLMProviderRequest): Promise<LLMProvider> {
    try {
      const response = await api.post<{ data: LLMProvider }>('/api/llm-providers', data);
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create LLM provider:', error);
      throw error;
    }
  },

  async update(providerId: string, data: UpdateLLMProviderRequest): Promise<LLMProvider> {
    try {
      const response = await api.put<{ data: LLMProvider }>(
        `/api/llm-providers/${providerId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to update LLM provider ${providerId}:`, error);
      throw error;
    }
  },

  async validateSchema(
    rawSchema: unknown
  ): Promise<
    { valid: true } | { valid: false; errors: Array<{ loc: Array<string | number>; msg: string }> }
  > {
    try {
      const response = await api.post<
        | { valid: true }
        | { valid: false; errors: Array<{ loc: Array<string | number>; msg: string }> }
      >('/api/llm-providers/validate-schema', rawSchema);
      // Why: validate-schema returns the body directly (not wrapped in ApiResponse envelope),
      // so response.data is the full result — no .data.data unwrap needed.
      return response.data;
    } catch (error) {
      // Why: the validate endpoint returns 422 with a structured error body
      // for malformed schemas; that's an expected outcome, not a network
      // failure. The api client throws on non-2xx by default, so unwrap the
      // 422 body when present and re-throw on actual network errors.
      const axiosErr = error as {
        response?: {
          status?: number;
          data?: {
            valid: false;
            errors: Array<{ loc: Array<string | number>; msg: string }>;
          };
        };
      };
      if (axiosErr?.response?.status === 422 && axiosErr.response.data) {
        return axiosErr.response.data;
      }
      logger.error('validate-schema endpoint failed', error);
      throw error;
    }
  },

  async delete(providerId: string): Promise<boolean> {
    try {
      const response = await api.delete<{ data: boolean }>(`/api/llm-providers/${providerId}`);
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to delete LLM provider ${providerId}:`, error);
      throw error;
    }
  },
};
